import { useEffect, useRef } from "react";
import Script from "next/script";
import styles from "./styles.module.scss";

const spacing = {
  none: "0",
  xs: "1",
  sm: "2",
  md: "3",
  lg: "4",
  xl: "5",
};

const spacingClass = (side, value) =>
  `p${side}-${spacing[value] ?? spacing.none}`;

const cloneScript = (node) => {
  const script = document.createElement("script");

  for (const { name, value } of node.attributes) {
    script.setAttribute(name, value);
  }

  script.async = node.hasAttribute("async");
  script.text = node.textContent;

  return script;
};

export const ScriptTag = ({
  className = "",
  id,
  src,
  code = "",
  strategy = "afterInteractive",
  paddingTop = "none",
  paddingBottom = "none",
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (strategy === "beforeInteractive") return;

    const container = containerRef.current;

    if (!container || container.dataset.scriptLoaded) return;
    container.dataset.scriptLoaded = "true";

    if (src) {
      const script = document.createElement("script");

      if (id) script.id = id;
      script.src = src;
      script.async = strategy === "lazyOnload";
      container.appendChild(script);

      return;
    }

    const markup = code.trim();

    if (!markup) return;

    if (markup.startsWith("<") || /<\s*script[\s>]/i.test(markup)) {
      const template = document.createElement("template");

      template.innerHTML = markup;
      template.content
        .querySelectorAll("script")
        .forEach((node) => node.replaceWith(cloneScript(node)));

      container.appendChild(template.content);

      return;
    }

    const script = document.createElement("script");

    if (id) script.id = id;
    script.text = code;
    container.appendChild(script);
  }, [id, src, code, strategy]);

  if (strategy === "beforeInteractive") {
    return src ? (
      <Script id={id} src={src} strategy={strategy} />
    ) : (
      <Script
        id={id}
        strategy={strategy}
        dangerouslySetInnerHTML={{
          __html: code,
        }}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.root} ${spacingClass("t", paddingTop)} ${spacingClass(
        "b",
        paddingBottom,
      )} ${className}`}
    />
  );
};

export default ScriptTag;
